import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import ErrorNotice from "../../errors/ErrorNotice";
import { useSubmission } from "../../shared/state/useSubmission";
import AppButton from "../buttons/AppButton";
import Field from "../forms/Field";
import type { ButtonVariant } from "../styles/buttonStyles";
import { FORM_CONTROL_CLASS } from "../styles/formStyles";
import ModalShell from "./ModalShell";

interface ConfirmActionProps {
  title: string;
  /** What will happen and what it cannot undo. */
  children: ReactNode;
  confirmLabel: string;
  /** `danger` for what removes or revokes something; `dangerSolid`, red, for what can't be
   * undone at all, such as deleting a project (F121). */
  tone?: Extract<ButtonVariant, "primary" | "danger" | "dangerSolid">;
  /** For what is hardest to undo, such as deleting a project (F121, GitHub's pattern): the
   * button works only once this text, such as the project's name, is typed exactly. */
  typedConfirmation?: string;
  /** The action itself; the dialog closes once it succeeds and stays open with the error if not. */
  onConfirm: () => Promise<unknown>;
  onClose: () => void;
}

/**
 * Asks before an action that is hard to undo. The action runs from the dialog, so the
 * dialog shows it pending, keeps a second press from running it twice, and shows a failure
 * where it happened.
 */
export default function ConfirmAction({ title, children, confirmLabel, tone = "primary", typedConfirmation, onConfirm, onClose }: ConfirmActionProps) {
  const { t } = useTranslation();
  const { state, submit } = useSubmission();
  const [typed, setTyped] = useState("");
  const pending = state.status === "submitting";
  const confirmed = typedConfirmation === undefined || typed === typedConfirmation;
  return (
    <ModalShell
      title={title}
      onClose={onClose}
      closeDisabled={pending}
      size="md"
      zIndexClassName="z-[60]"
      footer={
        <div className="flex justify-end gap-2">
          <AppButton variant="secondary" size="sm" disabled={pending} onClick={onClose}>{t("common.cancel")}</AppButton>
          <AppButton
            variant={tone}
            size="sm"
            disabled={pending || !confirmed}
            onClick={() => {
              submit(async () => {
                await onConfirm();
                onClose();
              });
            }}
          >
            {confirmLabel}
          </AppButton>
        </div>
      }
    >
      <div className="space-y-3 text-sm">
        {children}
        {typedConfirmation === undefined ? null : (
          <Field label={t("common.typeToConfirm", { text: typedConfirmation })}>
            {(control) => (
              <input {...control} className={FORM_CONTROL_CLASS} value={typed} autoComplete="off" spellCheck={false} disabled={pending}
                onChange={(event) => { setTyped(event.target.value); }} />
            )}
          </Field>
        )}
        {state.status === "failed" ? <ErrorNotice error={state.error} compact={true} /> : null}
      </div>
    </ModalShell>
  );
}
